import React, { useState, useEffect, useMemo } from "react";
import { Box, Grid, Paper, Typography, Skeleton } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";

// ─── Animated card ────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
`;

const KpiCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.kpiTokens?.radius?.lg ?? 12,
  padding: theme.spacing(2.25),
  background: theme.kpiTokens?.color?.surface ?? theme.palette.background.paper,
  border: `1px solid ${theme.kpiTokens?.color?.border ?? theme.palette.divider}`,
  position: "relative",
  overflow: "hidden",
  transition: "transform .18s, box-shadow .18s",
  animation: `${fadeUp} .4s ease both`,
  "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" },
}));

/**
 * Generic animated KPI card grid.
 *
 * Usage modes (pick one):
 *  1) Controlled:    <KPICards cards={defs} values={{key: 123, ...}} />
 *  2) Self-fetching: <KPICards cards={defs} fetchConfig={{ fetcher, url, mapResponse }} />
 *  3) Computed:      <KPICards cards={defs} data={rawArrayOrObj} computeValues={(data) => ({...})} />
 *
 * cards: [{ key, label, icon: IconComponent, colorHex, bg? }]
 *   - `bg` is optional; if omitted it's derived from colorHex.
 * columns: MUI Grid item sizing per card (default xs=6, md=3)
 * loading: external loading override (OR'd with internal fetch-loading)
 * emptyValue: shown when a value is null/undefined/"" (default 0)
 * formatValue: optional (value, cardDef) => displayValue, applied to every card's value
 */
const KPICards = ({
  cards,
  values: controlledValues,
  fetchConfig,       // { fetcher: (url) => Promise<any>, url: string, mapResponse?: (raw) => valuesObj }
  data,               // raw data for computed mode
  computeValues,      // (data) => valuesObj
  loading: loadingProp = false,
  columns = { xs: 6, md: 3 },
  spacing = 1.5,
  cardSx,
  emptyValue = 0,
  formatValue,
  onCardClick
}) => {
  const [fetchedValues, setFetchedValues] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(!!fetchConfig);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!fetchConfig?.fetcher || !fetchConfig?.url) return;
    let cancelled = false;
    setFetchLoading(true);
    setFetchError(null);

    fetchConfig
      .fetcher(fetchConfig.url)
      .then((raw) => {
        if (cancelled) return;
        const mapped = fetchConfig.mapResponse ? fetchConfig.mapResponse(raw) : raw;
        setFetchedValues(mapped);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchedValues({});
        setFetchError(err?.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchConfig?.url]);

  const computedValues = useMemo(() => {
    if (!computeValues) return null;
    return computeValues(data);
  }, [data, computeValues]);

  const values = controlledValues ?? fetchedValues ?? computedValues ?? {};
  const loading = loadingProp || fetchLoading;

  return (
    <Grid container spacing={spacing} sx={{ mb: 3 }}>
      {cards.map((cardDef, i) => {
        const { key, label, icon: Icon, colorHex, bg } = cardDef;
        const rawValue = values?.[key] ?? emptyValue;
        const displayValue = formatValue ? formatValue(rawValue, cardDef) : rawValue;

        return (
          <Grid item {...columns} key={key}>
            <KpiCard onClick={onCardClick ? () => onCardClick(cardDef, rawValue) : undefined}
              sx={{
                animationDelay: `${i * 60}ms`,
                ...(onCardClick && { cursor: "pointer" }),
                ...cardSx,
              }}>
              <Box
                sx={{
                  position: "absolute",
                  right: -12,
                  top: -12,
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: bg || `linear-gradient(135deg, ${colorHex}, ${colorHex}88)`,
                  opacity: 0.12,
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
                {Icon && (
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      bgcolor: `${colorHex}18`,
                      color: colorHex,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon fontSize="small" />
                  </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                  {loading ? (
                    <Skeleton width={40} height={28} />
                  ) : (
                    <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                      {displayValue}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7em" }}>
                    {label}
                  </Typography>
                </Box>
              </Box>
            </KpiCard>
          </Grid>
        );
      })}
      {fetchError && (
        <Grid item xs={12}>
          <Typography variant="caption" color="error">
            {fetchError}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default KPICards;